import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Share2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useT } from '@/i18n/useT';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { shareText } from '@/lib/share';
import { useDashboard } from '@/hooks/useDashboard';

export interface UnlockedAchievement {
  id: number;
  slug: string;
  titleEs: string;
  descriptionEs: string;
  icon: string;
  xp: number;
  tier: 'bronze' | 'silver' | 'gold' | 'legendary';
  confetti: boolean;
}

const TIER_LABEL: Record<UnlockedAchievement['tier'], string> = {
  bronze: 'Bronce',
  silver: 'Plata',
  gold: 'Oro',
  legendary: 'Legendario',
};

const TIER_COLOR: Record<UnlockedAchievement['tier'], string> = {
  bronze: '#b4763a',
  silver: '#8c9ba5',
  gold: '#d9a441',
  legendary: 'var(--xp)',
};

/**
 * Shows the achievements unlocked since the last visit, one at a time, and
 * tells the server they have been seen so they never pop twice.
 *
 * Mounted once in the layout: an unlock earned in any screen surfaces on the
 * next dashboard read without that screen knowing anything about it.
 */
export function Celebration() {
  const queryClient = useQueryClient();
  const { data } = useDashboard();
  const [queue, setQueue] = useState<UnlockedAchievement[]>([]);
  // Whether this batch of unlocks has already been queued + marked seen. Resets
  // only once the server confirms there's nothing pending, so a refetch can't
  // re-open the same celebration.
  const handledRef = useRef(false);

  const markSeen = useMutation({
    mutationFn: (ids: number[]) =>
      api('/api/achievements/seen', { method: 'POST', body: JSON.stringify({ ids }) }),
    // `celebrate` lives on the dashboard query, so invalidate that one (not just
    // the achievements list) — otherwise its stale copy re-queues the popup on
    // the next refetch or reload.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.achievements() });
    },
  });

  const pending = data?.celebrate;

  useEffect(() => {
    if (!pending || pending.length === 0) {
      handledRef.current = false;
      return;
    }
    if (handledRef.current) return;
    handledRef.current = true;
    setQueue(pending);
    markSeen.mutate(pending.map((a) => a.id));
    // markSeen is stable; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const current = queue[0];
  const next = () => setQueue((q) => q.slice(1));

  return (
    <AnimatePresence>
      {current && <CelebrationCard key={current.slug} achievement={current} onClose={next} />}
    </AnimatePresence>
  );
}

function CelebrationCard({
  achievement,
  onClose,
}: {
  achievement: UnlockedAchievement;
  onClose: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const t = useT();

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 px-6 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="celebration-title"
    >
      {achievement.confetti && !reducedMotion && <Confetti />}

      <motion.div
        className="relative w-full max-w-sm rounded-[var(--radius-card)] bg-surface p-7 text-center shadow-[var(--shadow-card)]"
        initial={{ scale: 0.85, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 24 }}
      >
        <p
          className="text-[12px] font-bold uppercase tracking-wider"
          style={{ color: TIER_COLOR[achievement.tier] }}
        >
          {TIER_LABEL[achievement.tier]}
        </p>

        <motion.div
          className="my-4 text-[64px]"
          aria-hidden
          initial={reducedMotion ? undefined : { scale: 0, rotate: -25 }}
          animate={reducedMotion ? undefined : { scale: 1, rotate: 0 }}
          transition={{ delay: 0.12, type: 'spring', stiffness: 260, damping: 14 }}
        >
          {achievement.icon}
        </motion.div>

        <h2
          id="celebration-title"
          className="text-[24px] font-bold leading-tight tracking-tight text-content"
        >
          {achievement.titleEs}
        </h2>
        <p className="mt-2 text-[15px] leading-relaxed text-muted">{achievement.descriptionEs}</p>

        <p className="mt-4 text-[14px] font-semibold" style={{ color: 'var(--xp)' }}>
          +{achievement.xp} XP
        </p>

        <div className="mt-6 flex gap-2">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => void shareText(t('share.achievement', { title: achievement.titleEs }))}
          >
            <Share2 size={17} aria-hidden />
            {t('common.share')}
          </Button>
          <Button className="flex-1" onClick={onClose}>
            {t('common.continue')}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/** Cheap confetti: 40 absolutely positioned pieces, no canvas, no library. */
function Confetti() {
  const COLORS = ['#16a97b', '#d9a441', '#8b5cf6', '#3b82c4', '#dc4c4c'];
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 1.5,
    color: COLORS[i % COLORS.length]!,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {pieces.map((piece) => (
        <motion.span
          key={piece.id}
          className="absolute top-[-5%] h-2.5 w-2 rounded-[1px]"
          style={{ left: `${piece.left}%`, background: piece.color }}
          initial={{ y: '-10vh', opacity: 1, rotate: piece.rotation }}
          animate={{ y: '110vh', opacity: [1, 1, 0], rotate: piece.rotation + 360 }}
          transition={{ duration: piece.duration, delay: piece.delay, ease: 'linear' }}
        />
      ))}
    </div>
  );
}
